import { useMutation, useQuery } from '@apollo/react-hooks';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Column, Row } from '../../../components/Flex';
import GraphQLErrorText from '../../../components/GraphQLErrorText';
import { XIcon } from '@heroicons/react/solid';
// import SelectBot from "../../../components/bots/SelectBot";
// import AddKey from "../../../components/bots/AddKey";
// import AddSpecial from "../../../components/bots/AddSpecialPrice";
import Link from 'next/link';
import Loading from '../../../components/Loading';
import useAuthOrRedirect from '../../../hooks/useAuthOrRedirect';
import {
  ARCHIVE_LISTING,
  UPDATE_LISTING,
} from '../../../lib/graphql/mutations';
import { GET_LISTING } from '../../../lib/graphql/queries';
import {
  arrayRemove,
  constructPriceInfo,
  isPriceInfoValid,
} from '../../../lib/utils';
import { COLORS } from '../../../theme';
import ListCal from '../../../components/bots/ListCal';
import moment from 'moment';
import { toast } from 'react-toastify';
import { isInvalidPriceChar } from '../../../utils/PriceValidator';
import { DateTime } from 'luxon';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import momentTimezone from 'moment-timezone';
import CreateListingEndCalendar from '../../../components/bots/CreateListingEndCalendar';
import { analytics } from '../../../lib/analytics';
import { TidalButton } from '../../../components/common/button';
import { WHITE_LABEL_ACCENT_COLOR } from '../../../lib/white-label';
import AddSpecial from '../../../components/bots/AddSpecialPrice';
import { formatCents, formatCentsAsUSD } from '../../../lib/currency';
import AddDiscount from '../../../components/bots/AddDiscountOption';
import DiscountsSections from '../../../components/bots/CreateListing/DiscountsSection';

const SelectedBotRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  grid-gap: 8px;

  & img {
    height: 32px;
    width: 32px;
    border-radius: 32px;
  }
`;

export const AddKeyInputField = styled.input`
  height: 32px;
  background: rgba(243, 244, 246);
  border-radius: 4px;
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  position: relative;
  padding-left: 1.225rem;
  padding-right: 2.225rem;
  max-width: 600px;

  /* Chrome, Safari, Edge, Opera */
  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  ::-moz-appearance: textfield;
`;

const AddKeySpacer = styled.div`
  width: 16px;
`;

const EditListing = () => {
  dayjs.extend(utc);
  let selectedBot;
  const router = useRouter();
  const [showAddSpecial, setShowAddSpecial] = useState(false);
  const [archiveListing, { loading: isDeleting }] =
    useMutation(ARCHIVE_LISTING);

  const [updateListing, { loading: isUpdating }] = useMutation(UPDATE_LISTING);

  const {
    loading,
    data: listingData,
    error,
  } = useQuery(GET_LISTING, {
    variables: {
      id: parseInt(router.query.id as string, 10),
    },
  });
  const { listing } = listingData || {};

  const [startDate, setStartDate] = useState(
    DateTime.fromJSDate(new Date(listing?.startDate)).setZone('utc'),
  );
  const [endDate, setEndDate] = useState(
    DateTime.fromJSDate(new Date(listing?.endDate)).setZone('utc'),
  );
  const [basePrice, setBasePriceCents] = useState(
    listing?.basePriceCents / 100,
  );
  const [exceptions, setExceptions] = useState(listing?.exceptions);
  const [discounts, setDiscounts] = useState([...(listing?.discounts || [])]);
  const [showAddDiscount, setShowAddDiscount] = useState(false);
  const [key1, setKey1] = useState('');
  const [key2, setKey2] = useState('');
  const [priceTouched, setPriceTouched] = useState(false);

  const onPriceInput = (e: any) => {
    if (isInvalidPriceChar(e.key)) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
  };

  useEffect(() => {
    if (!listing) return;
    setStartDate(
      DateTime.fromJSDate(new Date(listing?.startDate)).setZone('utc'),
    );
    setEndDate(DateTime.fromJSDate(new Date(listing?.endDate)).setZone('utc'));
    setBasePriceCents(listing?.basePriceCents / 100);
    const specialSpots = listing.availability.filter(
      (avaibility: any) => avaibility.priceCents != listing.basePriceCents,
    );
    const specialSpotsArray = [];
    specialSpots.forEach((element) => {
      specialSpotsArray.push({
        date: element.date,
        priceCents: element.priceCents,
        listingId: element.listingId,
        reservedByOrderId: element.reservedByOrderId,
      });
    });
    setExceptions(specialSpotsArray);
    setDiscounts([...listing.discounts]);

    setKey1(listing.email || listing.key);
    setKey2(listing.password || listing.secondaryKey);
    setPriceOptions({
      price: listing.basePriceCents / 100,
      rentalLength: moment(new Date(listing.endDate)).diff(
        moment(new Date(listing.startDate)),
        'days',
      ),
    });
    setNote(listing.note);
  }, [listing]);

  const onDelete = () => {
    if (confirm('Are you sure you want to delete this listing?')) {
      archiveListing({
        variables: {
          id: parseInt(router.query.id as string, 10),
        },
      })
        .then(() => {
          analytics.track('Archived Listing', {
            listingId: parseInt(router.query.id as string, 10),
          });
          router.push('/dashboard/transactions/listings');
        })
        .catch((err) => {
          toast(err.toString(), { type: 'error' });
        });
    }
  };

  const onUpdate = () => {
    if (basePrice < 5 || basePrice == null) {
      toast(
        'Please enter a valid price before continuing. Minimum value must be $5',
        {
          type: 'error',
        },
      );
      return;
    }

    if (exceptions.some((exc) => exc.price < basePrice * 100)) {
      toast('All special pricing must be higher then base price', {
        type: 'error',
      });
      return;
    }

    const discountErrorText =
      document.getElementById('discountError')?.innerText;
    if (discountErrorText && listing?.bot?.listingMode !== 'DIRECT') {
      toast(discountErrorText, {
        type: 'error',
      });
      return;
    }

    const data: any = {
      note,
      basePrice: basePrice * 100,
      startDate: momentTimezone
        .utc(startDate.toJSDate())
        .set({ hours: 21, minutes: 0, seconds: 0 })
        .format(),
      endDate: momentTimezone
        .utc(endDate.toJSDate())
        .set({ hours: 21, minutes: 0, seconds: 0 })
        .format(),
      exceptions: exceptions.filter((exc) => exc.priceCents !== null),
      discounts: discounts.map((discount) => ({
        dayCount: Number(discount.dayCount),
        percent: Number(discount.percent),
      })),
    };
    if (listing?.bot) {
      if (listing?.bot?.authType === '1key') {
        data.key = key1;
      }
      if (listing?.bot?.authType === '2key') {
        data.key = key1;
        data.secondaryKey = key2;
      }
      if (listing?.bot?.authType === 'login') {
        data.email = key1;
        data.password = key2;
      }
    }
    updateListing({
      variables: {
        id: parseInt(router.query.id as string, 10),
        updateListingData: data,
      },
    }).then(() => {
      analytics.track('Updated Listing', {
        listingId: parseInt(router.query.id as string, 10),
        startDate: momentTimezone
          .utc(startDate.toJSDate())
          .set({ hours: 21, minutes: 0, seconds: 0 })
          .format(),
        endDate: momentTimezone
          .utc(endDate.toJSDate())
          .set({ hours: 21, minutes: 0, seconds: 0 })
          .format(),
      });
      window.location.assign('/dashboard/transactions/listings');
    });
  };

  const [priceOptions, setPriceOptions] = useState({
    price: basePrice,
    rentalLength: undefined,
  });

  const [note, setNote] = useState('');

  useEffect(() => {
    analytics.page(`Rental Listing Details - ${router.query.id}`);
  }, []);

  const priceError = useMemo(() => {
    if (priceTouched) {
      if (basePrice < 5) {
        return 'Price must be at least $5.00';
      }
    }
    return null;
  }, [basePrice, priceTouched, discounts]);

  const removeDiscount = (discount) => {
    const a = arrayRemove(discounts, discount);
    setDiscounts(a);
  };

  const saveDiscount = (dayCount: number, percent: number) => {
    const newDiscount = {
      dayCount,
      percent,
    };

    const existingDiscountIdx = discounts.findIndex(
      (discount) => Number(discount.dayCount) === Number(dayCount),
    );
    if (existingDiscountIdx !== -1) {
      // handle updated discount
      discounts[existingDiscountIdx] = newDiscount;
      setDiscounts([...discounts]);
    } else {
      // handle new discount
      setDiscounts([...discounts, newDiscount]);
    }

    setShowAddDiscount(false);
  };

  if (!useAuthOrRedirect()) return null;
  if (loading) return <Loading />;
  if (error) return <GraphQLErrorText error={error} />;

  selectedBot = listing?.bot;

  let licenseValid = false;
  if (selectedBot.authType === 'login') {
    licenseValid = !!key1 && !!key2;
  } else if (selectedBot.authType === '1key') {
    licenseValid = !!key1;
  } else if (selectedBot.authType === '2key') {
    licenseValid = !!key1 && !!key2;
  }

  return (
    <div className="flex flex-col">
      {/* Body Container Desktop */}
      <div className="flex flex-col md:max-w-2xl min-w-max md:w-3/5 w-full px-4 mx-auto pt-10 md:pt-20 pb-10">
        <div className="flex justify-between">
          <span className="text-2xl font-bold text-gray-600 mb-6 text-left">
            Manage Listing
          </span>
          <Link href="/">
            <XIcon className="mr-1 h-8 w-8 text-gray-400 bg-gray-100 p-2 rounded-lg" />
          </Link>
        </div>

        <div className="pb-8 ">
          <div className="text-lg font-medium text-gray-600 mb-4 pb-2 text-left border-b-2 border-gray-100 w-full">
            Bot and Keys
          </div>

          <label className="text-gray-700 block text-md font-medium mt-6 mb-2">
            Selected bot
          </label>
          <SelectedBotRow>
            <img alt="" src={listing?.bot?.imageUrl} />
            <p>{listing?.bot?.name}</p>
          </SelectedBotRow>

          <label className="text-gray-700 block text-md font-medium mt-6 mb-0">
            My Key
          </label>
          <div className="flex flex-col justify-between w-full focus:z-10 border-b border-gray-300 shadow-sm py-2 bg-white text-sm font-medium gap-1">
            {listing?.bot?.authType === 'login' ? (
              <>
                <AddKeyInputField
                  type="text"
                  name="email"
                  id="email"
                  placeholder="Email"
                  value={key1}
                  aria-describedby="email-address"
                  onChange={(e) => {
                    setKey1(e.target.value);
                  }}
                />
                <AddKeySpacer />
                <AddKeyInputField
                  type="text"
                  name="password"
                  id="password"
                  placeholder="Password"
                  value={key2}
                  aria-describedby="password"
                  onChange={(e) => {
                    setKey2(e.target.value);
                  }}
                />
              </>
            ) : listing?.bot?.authType === '1key' ? (
              <AddKeyInputField
                type="text"
                name="key"
                id="key"
                placeholder="Key"
                value={key1}
                aria-describedby="key"
                onChange={(e) => {
                  setKey1(e.target.value);
                }}
              />
            ) : (
              <>
                <AddKeyInputField
                  type="text"
                  name="key"
                  id="key"
                  placeholder="Key"
                  value={key1}
                  aria-describedby="key"
                  onChange={(e) => {
                    setKey1(e.target.value);
                  }}
                />
                <AddKeySpacer />
                <AddKeyInputField
                  type="text"
                  name="secondary-key"
                  id="secondaryKey"
                  placeholder="Secondary Key"
                  value={key2}
                  aria-describedby="secondary-key"
                  onChange={(e) => {
                    setKey2(e.target.value);
                  }}
                />
              </>
            )}
          </div>

          <div className="pb-8 mt-8">
            <div className="flex justify-between border-b-2 border-gray-100 w-full mb-4 pb-2 items-center">
              <span className="text-lg font-medium text-gray-600  text-left ">
                Dates
              </span>
              <span className="hidden md:visible text-sm font-medium text-gray-400  text-right">
                For special dates, use special pricing below.
              </span>
            </div>

            <div className="pb-4">
              {/* Quick Select Start Day */}

              <div className="md:flex md:space-x-2 md:space-y-0 space-y-4">
                <div className="flex-1">
                  <label className="text-gray-700 block text-sm font-medium">
                    Start Day
                  </label>
                  {listing && (
                    <ListCal
                      sel={dayjs(startDate.toJSDate())}
                      setSel={(date) => {
                        setStartDate(DateTime.fromJSDate(new Date(date)));
                      }}
                    />
                  )}
                </div>
                <div className="flex-1">
                  <label className="text-gray-700 block text-sm font-medium">
                    End Day
                  </label>
                  {listing && (
                    <CreateListingEndCalendar
                      sel={dayjs(endDate.toJSDate())}
                      setSel={(date) => {
                        setEndDate(DateTime.fromJSDate(new Date(date)));
                      }}
                      listingMode={listing?.bot?.listingMode}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="pb-8">
            <div className="text-lg font-medium text-gray-600 mb-4 pb-2 text-left border-b-2 border-gray-100 w-full">
              Pricing
            </div>
            <div className="w-1/2">
              <label
                htmlFor="price"
                className="block text-sm font-medium text-gray-700"
              >
                Price per day
              </label>

              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">$</span>
                </div>
                <AddKeyInputField
                  type="number"
                  name="price"
                  id="price"
                  placeholder="10.00"
                  value={basePrice}
                  aria-describedby="price-currency"
                  onKeyPress={onPriceInput}
                  className={
                    priceError !== null ? `ring-red-500 border-red-500` : ``
                  }
                  onChange={(e) => {
                    setPriceTouched(true);
                    setBasePriceCents(Number(e.target.value));
                  }}
                />

                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <span
                    className="text-gray-500 sm:text-sm"
                    id="price-currency"
                  >
                    USD
                  </span>
                </div>
              </div>
              <label
                htmlFor="price"
                className="block text-xs pt-1 text-red-500"
              >
                {priceError}
              </label>
            </div>

            {exceptions && exceptions.length > 0 && (
              <>
                <div>
                  <label className="text-gray-700 block text-sm font-medium mt-6 mb-2">
                    Special Pricing
                  </label>
                </div>
                {exceptions?.map((special, index) => (
                  <div className="text-gray-700 flex justify-between w-full focus:z-10 items-center border-b border-gray-300 shadow-sm px-2 py-2 bg-white text-sm font-medium hover:bg-gray-50 focus:border-blue-500 focus:border-2 focus:ring-opacity-50 focus:outline-none focus:ring-4 focus:ring-offset-gray-100 focus:ring-blue-500">
                    <div className="flex flex-row">
                      {special.reservedByOrderId === null && (
                        <XIcon
                          onClick={() => {
                            const newExceptions = (exceptions as any[]).filter(
                              (i, idx) => idx !== index,
                            );
                            setExceptions(newExceptions);
                          }}
                          className="cursor-pointer w-4 h-4 text-gray-400 pt-1 mr-2"
                        />
                      )}
                      <span className="text-gray-500 text-sm mr-2">
                        {dayjs(special.date).format('L')}
                      </span>
                    </div>
                    <div className="mt-1 relative rounded-md shadow-sm w-1/2 float-right">
                      {special.reservedByOrderId === null && (
                        <>
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 sm:text-sm">$</span>
                          </div>
                          <AddKeyInputField
                            type="number"
                            name="price"
                            id="price"
                            placeholder="0.00"
                            value={
                              special.priceCents !== null
                                ? special.priceCents / 100
                                : null
                            }
                            aria-describedby="price-currency"
                            onKeyPress={onPriceInput}
                            onChange={(e) => {
                              const tempExceptions = [...exceptions];
                              const currentSpecialSpot = tempExceptions[index];
                              currentSpecialSpot.priceCents =
                                Number(e.target.value) * 100;
                              if (currentSpecialSpot.priceCents === 0) {
                                currentSpecialSpot.priceCents = null;
                              }
                              setExceptions(tempExceptions);
                            }}
                          />
                          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <span
                              className="text-gray-500 sm:text-sm"
                              id="price-currency"
                            >
                              USD
                            </span>
                          </div>
                        </>
                      )}
                      {special.reservedByOrderId !== null && (
                        <div className="flex items-center pointer-events-none justify-end">
                          <span className="h-[32px] items-center text-gray-500 pt-1">
                            Sold for {formatCentsAsUSD(special.priceCents)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}

            <div className="mt-2">
              {showAddSpecial ? (
                <AddSpecial
                  startDate={startDate}
                  endDate={endDate}
                  saveS={(sdate, sprice) => {
                    const newSpecial = {
                      date: sdate,
                      priceCents: Number(sprice) * 100,
                    };
                    const tempExceptions = [...exceptions];
                    const currentSpecialSpot = tempExceptions.find((exc) =>
                      dayjs(exc.date).isSame(sdate, 'day'),
                    );
                    if (currentSpecialSpot) {
                      currentSpecialSpot.priceCents = newSpecial.priceCents;
                    } else {
                      tempExceptions.push(newSpecial);
                    }
                    setExceptions(tempExceptions);
                  }}
                  showAdd={setShowAddSpecial}
                />
              ) : (
                <mark
                  onClick={() => setShowAddSpecial(true)}
                  className="text-blue-500 font-medium cursor-pointer text-sm bg-transparent"
                  style={{
                    color: `${WHITE_LABEL_ACCENT_COLOR}`,
                  }}
                >
                  Add a Special Price
                </mark>
              )}
            </div>

            <div className="mt-6 mb-2" data-tour="step-7">
              <DiscountsSections
                setDiscounts={setDiscounts}
                discounts={discounts}
                isConfirmState={false}
                priceOptions={priceOptions}
              />
            </div>
          </div>

          <div className="flex flex-row justify-end border-t-2 border-gray-100 pt-4 gap-2">
            <div className="w-1/2">
              <TidalButton
                onClick={() => !isDeleting && onDelete()}
                text={isDeleting ? 'Deleting...' : 'Delete'}
                isLoading={isDeleting}
                type="error"
                tint={false}
              />
            </div>
            <div className="w-1/2">
              <TidalButton
                onClick={() => !isUpdating && onUpdate()}
                text={isUpdating ? 'Updating...' : 'Save changes'}
                isLoading={isUpdating}
                type="info"
                tint={false}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export async function getServerSideProps() {
  return {
    props: {},
  };
}

export default EditListing;
